import { AdvancedSelect } from '../../../../widgets/select';
import { BasicDialogWindow, Dialog } from '../../../../widgets/dialog';
import { Columns } from '../../../../widgets/columns';
import { FormControl } from '../../../../widgets/formControl';
import { Input } from '../../../../widgets/input';
import { showNotification } from '../../../../widgets/notificationToaster';
import React, { createRef, FC, Suspense, useContext, useEffect, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { SingleValue } from 'react-select';
import { DialogOverlay } from 'src/components/pages/dashboard/intro/dialogDel';
import Loader from 'src/components/widgets/loader';
import { ValidationMessageStyled } from 'src/components/widgets/validation/validationMessage';
import { InputWithValidation, Separator } from 'src/shared/globals';
import { T, toLocalID, validateInputSecurity } from 'src/shared/helpers';
import { validateRequired, validationContext, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import { ModelColor } from '../../state/model/modelColor';
import { AdditionalCode, ModelColorMapping } from '../../state/model/modelColorMapping';
import { editVariantDialogQuery } from './__generated__/editVariantDialogQuery.graphql';
import { ColorAttribute, merchantProductOptionType } from './helpers';

type EditVariantDialogProps = {
    name?: string;
    buttonLabel?: string;
    mainSalesChannelID: string;
    mainSalesChannelRemoteUrl: string;
    modelColor: ModelColor;
    modelColorCode: ColorAttribute[];
    modelOptions: merchantProductOptionType[];
    additionalCodes: AdditionalCode[];
    colorMappings: ModelColorMapping[];
    onConfirm: (
        value: string,
        attributes: ColorAttribute[] | null | undefined,
        modelColorMapping: ModelColorMapping | null | undefined,
        onError: () => void
    ) => void;
};

type EditVariantQueryProps = {
    sku: string;
    salesChannelId: string;
    onMerchantProductLoaded: (items: merchantProductOptionType[]) => void;
};

const DialogOverlayEditVariant = styled(DialogOverlay)`
    z-index: 9999;
    justify-content: center;
`;

const HeaderColumns = styled(Columns)`
    strong {
        margin-bottom: 7px;
    }
`;

const AttributeSeparator = styled(Separator)`
    margin: 10px 0px 28px !important;
`;

const EditVariantDialogContent: FC<EditVariantDialogProps> = ({
    name,
    buttonLabel,
    mainSalesChannelID,
    mainSalesChannelRemoteUrl,
    modelColor,
    modelColorCode,
    modelOptions,
    additionalCodes,
    colorMappings,
    onConfirm
}) => {
    const validation = useContext(validationContext);
    // State
    const [variantName, setVariantName] = useState(name ?? '');
    const [dialogMode, setDialogMode] = useState<'EditVariant' | 'EditMapping'>('EditVariant');
    const [loadingMerchantAttributes, setLoadingMerchantAttributes] = useState(false);
    const [attributes, setAttributes] = useState<ColorAttribute[]>(modelColorCode.filter((a) => modelOptions.findIndex((mo) => mo.optionID == a.Id) >= 0));
    const [salesChannelSelectedID, setSalesChannelSelectedID] = useState(mainSalesChannelID);
    const [skuSelected, setSkuSelected] = useState(additionalCodes.find((ac) => ac.salesChannelId == mainSalesChannelID)?.code ?? '');
    const [merchantProductOptions, setMerchantProductOptions] = useState<merchantProductOptionType[]>([]);
    const [modelColorMappings, setModelColorMappings] = useState<ModelColorMapping[]>(colorMappings);
    const [isEditingVariantInProgress, setIsEditingVariantInProgress] = useState(false);

    const inputRef = createRef<HTMLInputElement>();

    // Callbacks
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleConfirm();
    };

    const handleChangeAttribute = (merchantOption: merchantProductOptionType, value: string) => {
        if (!value) {
            setAttributes((prevAttr) => prevAttr.filter((x) => x.Id !== merchantOption.optionID));
        } else {
            const indexMerchantOptionSelected = merchantOption.values.findIndex((x) => x.valueID === value);

            if (indexMerchantOptionSelected > -1) {
                const merchantOptionSelected = merchantOption.values[indexMerchantOptionSelected];
                const newOptionValue = {
                    Id: merchantOptionSelected.valueID,
                    Label: merchantOptionSelected.label
                };

                setAttributes((prevAttr) => {
                    let newAttributes = [...prevAttr];
                    let optionToChange = newAttributes.find((x) => x.Id == merchantOption.optionID);
                    if (optionToChange) {
                        optionToChange.Label = merchantOption.label; // Update  attribute label too if changed on the store
                        optionToChange.Value = newOptionValue;
                    } else {
                        newAttributes.push({
                            Id: merchantOption.optionID,
                            Label: merchantOption.label,
                            Value: newOptionValue
                        });
                    }

                    return newAttributes;
                });
            }
        }
    };

    const handleConfirm = () => {
        if (validation.isValid() === false) {
            showNotification({
                type: 'danger',
                message: T._('Some fields are invalid. Please correct them before confirming.', 'Admin'),
                dismiss: {
                    duration: 3000
                }
            });
            return;
        }

        if (attributes.length) {
            setIsEditingVariantInProgress(true);

            let modelColorMapping = modelColorMappings.find((mcm) => toLocalID(mcm.salesChannelID) == toLocalID(salesChannelSelectedID));
            if (modelColorMapping) {
                let merchantProdutAttributesMapping = JSON.parse(modelColorMapping.merchantCode || '[]') as ColorAttribute[];
                if ([...new Set(merchantProdutAttributesMapping.map((mpa) => mpa.Id))].length != merchantProdutAttributesMapping.length) {
                    showNotification({
                        type: 'danger',
                        message: (
                            <>
                                {T._(
                                    'Unable to update product variants mapping. Every attributes must be mapped with different merchant product attributes',
                                    'Admin'
                                )}
                            </>
                        ),
                        dismiss: {
                            duration: 3000
                        }
                    });
                    setIsEditingVariantInProgress(false);
                    return;
                }
            }

            onConfirm(variantName, attributes, modelColorMapping, () => {
                setIsEditingVariantInProgress(false);
            });
        } else {
            // update only the name
            onConfirm(variantName, null, null, () => {
                setIsEditingVariantInProgress(false);
            });
        }
    };

    const onChangeSourceVariants = (item: SingleValue<{ value: string; label: string }>) => {
        let newSalesChannelID = item?.value ?? mainSalesChannelID;
        setSalesChannelSelectedID(newSalesChannelID);
        setSkuSelected(additionalCodes.find((ac) => ac.salesChannelId == newSalesChannelID)?.code ?? '');
        setLoadingMerchantAttributes(true);
        toLocalID(newSalesChannelID) == toLocalID(mainSalesChannelID) ? setDialogMode('EditVariant') : setDialogMode('EditMapping');
    };

    const onChangeAttributeMapping = (item: SingleValue<{ value: string; label: string }>, indexAttributeOfflineProduct: number) => {
        let query = modelColorMappings.find((mcm) => toLocalID(mcm.salesChannelID) == toLocalID(salesChannelSelectedID));
        if (!query) return;

        let newModelColorMapping = { ...query };
        let newModelColorMappings = [...modelColorMappings.filter((mcm) => toLocalID(mcm.salesChannelID) != toLocalID(salesChannelSelectedID))];

        let merchantColorAttribute = JSON.parse(newModelColorMapping.merchantCode || '[]') as ColorAttribute[];
        let option = merchantProductOptions.find((mo) => mo.optionID == item?.value)?.values[0]!;

        merchantColorAttribute[indexAttributeOfflineProduct].Id = item?.value!;
        merchantColorAttribute[indexAttributeOfflineProduct].Label = item?.label!;
        merchantColorAttribute[indexAttributeOfflineProduct].Value.Label = option.label;
        merchantColorAttribute[indexAttributeOfflineProduct].Value.Id = option.valueID;
        newModelColorMapping.merchantCode = JSON.stringify(merchantColorAttribute);
        newModelColorMappings.push(newModelColorMapping);

        setModelColorMappings(newModelColorMappings);
    };

    const onChangeOptionMapping = (item: SingleValue<{ value: string; label: string }>, indexAttributeOfflineProduct: number) => {
        let query = modelColorMappings.find((mcm) => toLocalID(mcm.salesChannelID) == toLocalID(salesChannelSelectedID));
        if (!query) return;

        let newModelColorMapping = { ...query };
        let newModelColorMappings = [...modelColorMappings.filter((mcm) => toLocalID(mcm.salesChannelID) != toLocalID(salesChannelSelectedID))];

        let merchantColorAttribute = JSON.parse(newModelColorMapping.merchantCode || '[]') as ColorAttribute[];
        let option = merchantProductOptions
            .find((mo) => mo.optionID == merchantColorAttribute[indexAttributeOfflineProduct].Id)
            ?.values.find((v) => v.valueID == item?.value)!;

        merchantColorAttribute[indexAttributeOfflineProduct].Value.Label = option?.label ?? 'No map';
        merchantColorAttribute[indexAttributeOfflineProduct].Value.Id = option?.valueID ?? '';
        newModelColorMapping.merchantCode = JSON.stringify(merchantColorAttribute);

        newModelColorMappings.push(newModelColorMapping);
        setModelColorMappings(newModelColorMappings);
    };

    const onMerchantProductLoaded = (items: merchantProductOptionType[]) => {
        let modelColorMapping = modelColorMappings.find((mcm) => toLocalID(mcm.salesChannelID) == toLocalID(salesChannelSelectedID));
        if (items.length >= modelColorCode.length) {
            let newModelColorMapping = modelColorMapping
                ? { ...modelColorMapping }
                : ({
                      colorID: modelColor.colorID,
                      merchantCode: '[]',
                      salesChannelID: salesChannelSelectedID,
                      productSKU: '',
                      productColor: {
                          code: modelColor.code,
                          guid: modelColor.guid,
                          colorID: modelColor.colorID,
                          name: modelColor.name
                      }
                  } as ModelColorMapping);

            let merchantMappingCode = JSON.parse(modelColorMapping?.merchantCode || '[]') as ColorAttribute[];
            let newMerchantMappingCode = [...merchantMappingCode];

            if (!modelColorMapping) {
                items.forEach((attribute) => {
                    newMerchantMappingCode.push({
                        Id: attribute.optionID,
                        Label: attribute.label,
                        Value: {
                            Id: '',
                            Label: 'No map'
                        }
                    });
                });
            } else if (merchantMappingCode.length < modelColorCode.length) {
                let newAttributes = [...items.filter((item) => merchantMappingCode.some((ncm) => ncm.Id != item.optionID))];
                newAttributes.forEach((newAttribute) => {
                    newMerchantMappingCode.push({
                        Id: newAttribute.optionID,
                        Label: newAttribute.label,
                        Value: {
                            Id: newAttribute.values[0].valueID,
                            Label: newAttribute.values[0].label
                        }
                    });
                });
            }

            let newModelColorMappings = [...modelColorMappings.filter((m) => toLocalID(m.salesChannelID) != toLocalID(salesChannelSelectedID))];

            newModelColorMapping.merchantCode = JSON.stringify(newMerchantMappingCode);
            newModelColorMappings.push(newModelColorMapping);
            setModelColorMappings(newModelColorMappings);
        } else if (items.length == 0) {
            let newModelColorMapping =
                (modelColor.colorID ?? -1) > 0
                    ? {
                          colorID: modelColor.colorID ?? -1,
                          salesChannelID: salesChannelSelectedID,
                          merchantCode: '[]'
                      }
                    : ({
                          colorID: modelColor.colorID ?? -1,
                          salesChannelID: salesChannelSelectedID,
                          merchantCode: '[]',
                          productColor: {
                              code: modelColor.code,
                              colorID: modelColor.colorID,
                              guid: modelColor.guid,
                              name: modelColor.name
                          }
                      } as ModelColorMapping);

            let newModelColorMappings = [...modelColorMappings.filter((m) => toLocalID(m.salesChannelID) != toLocalID(salesChannelSelectedID))];
            newModelColorMappings.push(newModelColorMapping);
            setModelColorMappings(newModelColorMappings);
        }

        setMerchantProductOptions(items);
        setLoadingMerchantAttributes(false);
    };

    // Option combo source variants
    const sourceVariantOptions = [
        {
            value: mainSalesChannelID,
            label: mainSalesChannelRemoteUrl
        },

        ...additionalCodes.map((ac) => {
            return {
                value: ac.salesChannelId,
                label: ac.remoteUrl ?? ''
            };
        })
    ];

    // Options for mapping attributes combo
    const merchantProductAttributes = merchantProductOptions.map((mpo) => {
        return { label: mpo.label, value: mpo.optionID };
    });
    const merchantAttributeMapping = JSON.parse(
        modelColorMappings.find((mcm) => toLocalID(mcm.salesChannelID) == toLocalID(salesChannelSelectedID))?.merchantCode || '[]'
    ) as ColorAttribute[];

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    return (
        <Dialog
            windowDecorator={BasicDialogWindow}
            buttons={[
                {
                    label: buttonLabel || T._('Confirm ', 'Admin'),
                    onClick: handleConfirm,
                    disabled: isEditingVariantInProgress
                }
            ]}
        >
            {dialogMode == 'EditMapping' && loadingMerchantAttributes && (
                <Suspense
                    fallback={
                        <DialogOverlayEditVariant>
                            <Loader />
                        </DialogOverlayEditVariant>
                    }
                >
                    <EditVariantQuery salesChannelId={salesChannelSelectedID} sku={skuSelected} onMerchantProductLoaded={onMerchantProductLoaded} />
                </Suspense>
            )}

            {sourceVariantOptions.length > 1 && (
                <FormControl label={T._('Source', 'Admin')}>
                    <AdvancedSelect
                        onChange={onChangeSourceVariants}
                        menuPosition={'fixed'}
                        value={sourceVariantOptions.find((o) => toLocalID(o.value) == toLocalID(salesChannelSelectedID))}
                        options={sourceVariantOptions}
                    ></AdvancedSelect>
                </FormControl>
            )}

            {dialogMode == 'EditVariant' ? (
                <>
                    <FormControl label={T._('Variation name', 'Admin')}>
                        <InputWithValidation
                            ref={inputRef}
                            onKeyDown={handleKeyDown}
                            value={variantName}
                            onChange={(e) => setVariantName(e.currentTarget.value)}
                            validationKey='validationVariantName'
                            validate={(value) => validateRequired(value) || validateInputSecurity(value).isValid === false}
                            message={`${
                                validateRequired(variantName)
                                    ? T._('Required', 'Admin')
                                    : !validateInputSecurity(variantName).isValid
                                      ? T._(`Invalid characters detected: ${validateInputSecurity(variantName).invalidCharacters.join(', ')}`, 'Admin')
                                      : ''
                            }`}
                        />
                    </FormControl>

                    {modelOptions.map((opt, i) => {
                        const options = opt.values.map((val) => ({ value: val.valueID, label: val.label }));
                        return (
                            <FormControl key={opt.optionID} label={opt.label}>
                                <AdvancedSelect
                                    onChange={(selectedOption) => handleChangeAttribute(opt, selectedOption?.value ?? '')}
                                    menuPosition='fixed'
                                    value={{
                                        value: attributes.find((x) => x.Id == opt.optionID)?.Value.Id,
                                        label: attributes.find((x) => x.Id == opt.optionID)?.Value.Label
                                    }}
                                    options={options}
                                />
                                {!attributes.length && <ValidationMessageStyled>{T._('At least one field is required', 'Admin')}</ValidationMessageStyled>}
                            </FormControl>
                        );
                    })}
                </>
            ) : dialogMode == 'EditMapping' && merchantProductOptions.length > 0 && merchantAttributeMapping.length > 0 ? (
                <>
                    <HeaderColumns $xs={2}>
                        <strong>{T._('Main product', 'Admin')}</strong>
                        <strong>{T._('Sales channel product', 'Admin')}</strong>
                    </HeaderColumns>
                    <div>
                        {modelColorCode.map((ma, i) => {
                            let merchantProductOptionsForMapping = [{ label: 'No map', value: '' }].concat(
                                merchantProductOptions
                                    .find((mpo) => mpo.optionID == merchantAttributeMapping[i].Id)
                                    ?.values.map((o) => {
                                        return { value: o.valueID, label: o.label };
                                    })!
                            );

                            return (
                                <React.Fragment key={ma.Id}>
                                    <Columns $xs={2}>
                                        <FormControl>
                                            <Input readOnly={true} value={ma.Label}></Input>
                                        </FormControl>
                                        <FormControl>
                                            <AdvancedSelect
                                                isSearchable={false}
                                                options={merchantProductAttributes}
                                                className='comboControl'
                                                menuPosition='fixed'
                                                value={merchantProductAttributes.find((mpa) => mpa.value == merchantAttributeMapping[i].Id)}
                                                onChange={(item) => onChangeAttributeMapping(item, i)}
                                            />
                                        </FormControl>
                                    </Columns>

                                    <Columns $xs={2}>
                                        <FormControl>
                                            <Input readOnly={true} value={ma.Value.Label}></Input>
                                        </FormControl>
                                        <FormControl>
                                            <AdvancedSelect
                                                isSearchable={false}
                                                options={merchantProductOptionsForMapping}
                                                className='comboControl'
                                                menuPosition='fixed'
                                                value={{ value: merchantAttributeMapping[i].Value.Id, label: merchantAttributeMapping[i].Value.Label }}
                                                onChange={(item) => onChangeOptionMapping(item, i)}
                                            />
                                        </FormControl>
                                    </Columns>

                                    <AttributeSeparator></AttributeSeparator>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </>
            ) : (
                <>
                    <AdvancedSelect
                        //onChange={selectedOption => handleChangeAttribute(opt, selectedOption?.value ?? '')}
                        menuPosition='fixed'
                        value={{ value: modelColor.guid, label: modelColor.name }}
                        options={[{ value: modelColor.guid, label: modelColor.name }]}
                    />
                </>
            )}
        </Dialog>
    );
};

const EditVariantQuery: FC<EditVariantQueryProps> = ({ sku, salesChannelId, onMerchantProductLoaded }) => {
    const { merchantProductOptions } = useLazyLoadQuery<editVariantDialogQuery>(
        graphql`
            query editVariantDialogQuery($sku: String!, $salesChannelID: ID!) {
                merchantProductOptions(sku: $sku, salesChannelID: $salesChannelID) {
                    optionID
                    label
                    values {
                        valueID
                        label
                    }
                }
            }
        `,
        { sku, salesChannelID: salesChannelId }
    );

    useEffect(() => {
        merchantProductOptions && onMerchantProductLoaded(merchantProductOptions as merchantProductOptionType[]);
    }, []);

    return null;
};

const EditVariantDialog: FC<EditVariantDialogProps> = (props) => {
    return (
        <ValidationStateProvider>
            <EditVariantDialogContent {...props} />
        </ValidationStateProvider>
    );
};

export default EditVariantDialog;
